import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { UserPlus, Download, Search, UploadCloud, X, Filter, CreditCard, ExternalLink, ChevronLeft, ChevronRight, CheckSquare, Square, Minus, CalendarCheck } from 'lucide-react';
import { useEmployees, useDepartments } from '@/api/queries/employee.queries';
import { useCompanySettings } from '@/api/queries/settings.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BulkUploadModal } from '@/components/employees/BulkUploadModal';
import { ExportModal } from '@/components/employees/ExportModal';
import { BulkEditModal } from '@/components/employees/BulkEditModal';
import { BulkAttendanceModal } from '@/components/employees/BulkAttendanceModal';
import { cn } from '@/lib/cn';

const EMPLOYMENT_LABELS = { FULL_TIME:'Full Time', PART_TIME:'Part Time', CONTRACT:'Contract', DAILY_WAGE:'Daily Wage' };
const JOINED_WINDOWS = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];
const PAGE_SIZE = 50;

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');
  const [status, setStatus] = useState('');
  const [empType, setEmpType] = useState('');
  const [joinedWithin, setJoinedWithin] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkAttendance, setShowBulkAttendance] = useState(false);

  const { data: empResp } = useEmployees();
  const allEmployees = empResp?.data || [];

  const { data: deptsResp } = useDepartments();
  const departments = deptsResp?.data || [];

  const { data: settingsResp } = useCompanySettings();
  const enabledList = settingsResp?.data?.settings?.employee_form_fields || [];

  const filtered = useMemo(() => allEmployees.filter(e => {
    const q = search.toLowerCase();
    const matchQ = !q || (e.first_name||'').toLowerCase().includes(q) || (e.last_name||'').toLowerCase().includes(q) || (e.employee_code||'').toLowerCase().includes(q) || (e.email||'').toLowerCase().includes(q) || (e.designation||'').toLowerCase().includes(q);
    const deptName = e.department?.name || (typeof e.department === 'string' ? e.department : '');
    const matchD = !dept || deptName === dept;
    const matchS = !status || e.status === status;
    const matchT = !empType || e.employment_type === empType;
    const matchJ = !joinedWithin || (e.joining_date && (Date.now() - new Date(e.joining_date).getTime()) <= Number(joinedWithin) * 86400000);
    return matchQ && matchD && matchS && matchT && matchJ;
  }), [allEmployees, search, dept, status, empType, joinedWithin]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [search, dept, status, empType, joinedWithin]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterSummary = [
    search && `Search: "${search}"`,
    dept && `Department: ${dept}`,
    status && `Status: ${status}`,
    empType && `Type: ${EMPLOYMENT_LABELS[empType]}`,
    joinedWithin && `Joined: ${JOINED_WINDOWS.find(w=>w.value===joinedWithin)?.label}`,
  ].filter(Boolean).join(' · ');

  // Checkbox helpers
  const allPageSelected = paginated.length > 0 && paginated.every(e => selectedIds.has(e.id));
  const somePageSelected = paginated.some(e => selectedIds.has(e.id)) && !allPageSelected;

  const toggleAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) { paginated.forEach(e => next.delete(e.id)); }
      else { paginated.forEach(e => next.add(e.id)); }
      return next;
    });
  };
  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectedEmployees = allEmployees.filter(e => selectedIds.has(e.id));

  const selectCls = 'h-11 px-3 min-w-[130px] text-[13px] bg-transparent text-[var(--text-primary)] focus:outline-none focus:bg-[var(--bg-muted)] transition-colors cursor-pointer appearance-none';

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Employees"
        subtitle={`${allEmployees.length} total employees`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" icon={ExternalLink} onClick={() => navigate('/employees/id-cards', selectedIds.size > 0 ? { state: { preselected: [...selectedIds] } } : undefined)}>ID Cards & Info</Button>
            <Button variant="outline" size="sm" icon={UploadCloud} onClick={() => setShowBulkUpload(true)}>Bulk Upload</Button>
            <Button variant="outline" size="sm" icon={Download} onClick={() => setShowExport(true)}>Export</Button>
            <Button size="sm" icon={UserPlus} onClick={() => navigate('/employees/new')}>Add Employee</Button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="card p-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-0 divide-y sm:divide-y-0 sm:divide-x divide-[var(--border)]">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"/>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, code, email, designation…"
              className="w-full h-11 pl-9 pr-3 text-[13px] bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:bg-[var(--bg-muted)] transition-colors"
            />
          </div>
          <div className="flex flex-wrap sm:flex-nowrap divide-x divide-[var(--border)]">
            <select value={dept} onChange={e => setDept(e.target.value)} className={selectCls}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
            </select>
            <select value={status} onChange={e => setStatus(e.target.value)} className={selectCls}>
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ON_LEAVE">On Leave</option>
              <option value="TERMINATED">Terminated</option>
            </select>
            <select value={empType} onChange={e => setEmpType(e.target.value)} className={selectCls}>
              <option value="">All Types</option>
              {Object.entries(EMPLOYMENT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={joinedWithin} onChange={e => setJoinedWithin(e.target.value)} className={selectCls}>
              <option value="">Join Date: Any</option>
              {JOINED_WINDOWS.filter(w => w.value).map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </div>
        </div>

        {(search || dept || status || empType || joinedWithin) && (
          <div className="flex flex-wrap items-center gap-1.5 px-3.5 py-2 border-t border-[var(--border)] bg-[var(--bg-muted)]">
            <Filter size={12} className="text-[var(--text-tertiary)] shrink-0"/>
            {search && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">"{search}" <button onClick={() => setSearch('')}><X size={10}/></button></span>}
            {dept && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">{dept} <button onClick={() => setDept('')}><X size={10}/></button></span>}
            {status && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">{status} <button onClick={() => setStatus('')}><X size={10}/></button></span>}
            {empType && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">{EMPLOYMENT_LABELS[empType]} <button onClick={() => setEmpType('')}><X size={10}/></button></span>}
            {joinedWithin && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">{JOINED_WINDOWS.find(w=>w.value===joinedWithin)?.label} <button onClick={() => setJoinedWithin('')}><X size={10}/></button></span>}
            <button onClick={() => { setSearch(''); setDept(''); setStatus(''); setEmpType(''); setJoinedWithin(''); }}
              className="ml-auto text-[11px] text-[var(--text-tertiary)] hover:text-[var(--danger)] flex items-center gap-0.5 font-medium">
              <X size={11}/> Clear all
            </button>
            <span className="text-[11px] text-[var(--text-tertiary)] ml-1">· {filtered.length} of {allEmployees.length}</span>
          </div>
        )}
      </div>

      {showBulkUpload && <BulkUploadModal onClose={() => setShowBulkUpload(false)} enabledList={enabledList} departments={departments} allEmployees={allEmployees}/>}
      {showExport && <ExportModal onClose={() => setShowExport(false)} employees={filtered} enabledList={enabledList} filterSummary={filterSummary}/>}
      {showBulkEdit && <BulkEditModal initialSelectedIds={selectedIds.size > 0 ? selectedIds : null} onClose={() => { setShowBulkEdit(false); setSelectedIds(new Set()); }}/>}

      {/* Desktop table */}
      <div className="card p-0 overflow-x-auto hidden sm:block">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="px-4 py-3 w-10">
                <button onClick={toggleAll} className="flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--accent)]">
                  {allPageSelected ? <CheckSquare size={15} className="text-[var(--accent)]"/> : somePageSelected ? <Minus size={15}/> : <Square size={15}/>}
                </button>
              </th>
              {['Employee','Department','Type','Joining Date','Today','Status'].map(h => (
                <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {paginated.map(e => (
              <tr key={e.id}
                className={cn('transition-colors cursor-pointer',
                  selectedIds.has(e.id) ? 'bg-[var(--accent)]/5' : 'hover:bg-[var(--bg-muted)]',
                  e.status === 'TERMINATED' && 'opacity-60'
                )}>
                <td className="px-4 py-3.5" onClick={ev => { ev.stopPropagation(); toggleOne(e.id); }}>
                  <button className="flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--accent)]">
                    {selectedIds.has(e.id) ? <CheckSquare size={15} className="text-[var(--accent)]"/> : <Square size={15}/>}
                  </button>
                </td>
                <td className="px-4 py-3.5" onClick={() => navigate(`/employees/${e.id}`)}>
                  <div className="flex items-center gap-3">
                    <Avatar name={`${e.first_name} ${e.last_name}`} src={e.photo_url} size="sm"/>
                    <div>
                      <p className={cn('text-[13px] font-semibold text-[var(--text-primary)]', e.status === 'TERMINATED' && 'line-through text-[var(--text-tertiary)]')}>
                        {e.first_name} {e.last_name}
                      </p>
                      <p className="text-[11px] text-[var(--text-tertiary)]">{e.employee_code} · {e.designation}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5 text-[13px] text-[var(--text-secondary)]" onClick={() => navigate(`/employees/${e.id}`)}>
                  {e.department?.name || (typeof e.department === 'string' ? e.department : '—')}
                </td>
                <td className="px-4 py-3.5" onClick={() => navigate(`/employees/${e.id}`)}>
                  <Badge variant={e.employment_type==='FULL_TIME'?'blue':e.employment_type==='CONTRACT'?'purple':'default'} size="sm">
                    {EMPLOYMENT_LABELS[e.employment_type]}
                  </Badge>
                </td>
                <td className="px-4 py-3.5 text-[13px] text-[var(--text-secondary)]" onClick={() => navigate(`/employees/${e.id}`)}>{e.joining_date}</td>
                <td className="px-4 py-3.5" onClick={() => navigate(`/employees/${e.id}`)}>
                  {e.today_attendance ? (
                    <div>
                      <StatusBadge status={e.today_attendance.status}/>
                      {e.today_attendance.check_in && (
                        <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{e.today_attendance.check_in}–{e.today_attendance.check_out||'–'}</p>
                      )}
                    </div>
                  ) : <span className="text-[12px] text-[var(--text-tertiary)]">–</span>}
                </td>
                <td className="px-4 py-3.5" onClick={() => navigate(`/employees/${e.id}`)}><StatusBadge status={e.status}/></td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No employees found</td></tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-muted)]">
            <p className="text-[12px] text-[var(--text-tertiary)]">
              Showing {Math.min((page-1)*PAGE_SIZE+1, filtered.length)}–{Math.min(page*PAGE_SIZE, filtered.length)} of {filtered.length} employees
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                className="p-1.5 rounded-[6px] hover:bg-[var(--bg-elevated)] disabled:opacity-40 disabled:cursor-not-allowed text-[var(--text-secondary)]">
                <ChevronLeft size={14}/>
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const pg = totalPages <= 7 ? i+1 : page <= 4 ? i+1 : page >= totalPages-3 ? totalPages-6+i : page-3+i;
                return (
                  <button key={pg} onClick={() => setPage(pg)}
                    className={cn('w-7 h-7 rounded-[6px] text-[12px] font-semibold transition-colors',
                      pg === page ? 'bg-[var(--accent)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                    )}>{pg}</button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                className="p-1.5 rounded-[6px] hover:bg-[var(--bg-elevated)] disabled:opacity-40 disabled:cursor-not-allowed text-[var(--text-secondary)]">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {paginated.map(e => (
          <div key={e.id} className={cn('card flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform', e.status === 'TERMINATED' && 'opacity-60')}>
            <button onClick={ev => { ev.stopPropagation(); toggleOne(e.id); }} className="shrink-0 text-[var(--text-tertiary)]">
              {selectedIds.has(e.id) ? <CheckSquare size={16} className="text-[var(--accent)]"/> : <Square size={16}/>}
            </button>
            <div className="flex items-center gap-3 flex-1" onClick={() => navigate(`/employees/${e.id}`)}>
              <Avatar name={`${e.first_name} ${e.last_name}`} src={e.photo_url} size="md"/>
              <div className="flex-1 min-w-0">
                <p className={cn('text-[14px] font-semibold text-[var(--text-primary)]', e.status === 'TERMINATED' && 'line-through')}>{e.first_name} {e.last_name}</p>
                <p className="text-[12px] text-[var(--text-secondary)]">{e.designation} · {e.department?.name || (typeof e.department === 'string' ? e.department : '—')}</p>
                <p className="text-[11px] text-[var(--text-tertiary)]">{e.employee_code}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <StatusBadge status={e.status}/>
              {e.today_attendance && <StatusBadge status={e.today_attendance.status}/>}
            </div>
          </div>
        ))}
        {paginated.length === 0 && (
          <div className="card text-center text-[14px] text-[var(--text-tertiary)] py-8">No employees found</div>
        )}
        {/* Mobile pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 py-2">
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              className="flex items-center gap-1 text-[13px] font-semibold text-[var(--accent)] disabled:opacity-40">
              <ChevronLeft size={14}/> Prev
            </button>
            <span className="text-[12px] text-[var(--text-tertiary)]">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              className="flex items-center gap-1 text-[13px] font-semibold text-[var(--accent)] disabled:opacity-40">
              Next <ChevronRight size={14}/>
            </button>
          </div>
        )}
      </div>

      {/* Bulk Attendance Modal */}
      <BulkAttendanceModal
        open={showBulkAttendance}
        onClose={() => setShowBulkAttendance(false)}
        employees={selectedEmployees}
      />

      {/* Floating bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[16px] shadow-[var(--shadow-xl)] px-5 py-3 fade-in">
          <span className="text-[13px] font-semibold text-[var(--text-primary)]">{selectedIds.size} selected</span>
          <div className="w-px h-5 bg-[var(--border)]"/>
          <Button size="sm" icon={CalendarCheck} onClick={() => setShowBulkAttendance(true)}>Mark Attendance</Button>
          <Button size="sm" icon={ExternalLink} variant="outline" onClick={() => navigate('/employees/id-cards', { state: { preselected: [...selectedIds] } })}>ID Cards & Info</Button>
          <Button size="sm" variant="outline" onClick={() => setShowBulkEdit(true)}>Bulk Edit</Button>
          <Button size="sm" variant="outline" onClick={() => setShowExport(true)}>Export</Button>
          <button onClick={() => setSelectedIds(new Set())} className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--danger)]"><X size={15}/></button>
        </div>
      )}
    </div>
  );
}
